import { supabase } from '@/lib/supabase/client';
import { createNotFoundError, fromPostgrestError, normalizeError } from '@/lib/services/errors';
import type { Database, Json } from '@/lib/supabase/types.gen';
import type {
  AvailabilitiesPageContent,
  AboutContent,
  AboutPortfolioContent,
  AboutPortfolioStat,
  AboutValuePillar,
  AboutValuesContent,
  BrandContent,
  ContactPageContent,
  ContactHour,
  FooterContent,
  HomeHeroMediaType,
  HomeContent,
  LeadCaptureContent,
  LeadBenefit,
  LeadFormContent,
  LeadStat,
  LeadershipContact,
  NavigationChildLink,
  NavigationContent,
  NavigationLink,
  NavigationLinkAction,
  NavigationLinkStyle,
  NavigationShortcut,
  PublicContent,
  PropertyDetailContent,
  SharedPublicContent,
  ShowingDialogContent,
  SeoContent,
  SiteSettings,
  SiteSettingsUpdateInput,
  UnitDetailContent,
} from '@/lib/types/siteSettings';
import type { PostgrestError, PostgrestSingleResponse } from '@supabase/postgrest-js';

type SiteSettingsRow = Database['public']['Tables']['site_settings']['Row'];
type SiteSettingsUpdate = Database['public']['Tables']['site_settings']['Update'];

type NavigationLinkWithDefaults = NavigationLink & {
  action: NavigationLinkAction;
  style: NavigationLinkStyle;
};

type SerializedNavigationChild = {
  label: string;
  enabled: boolean;
  external: boolean;
  order: number;
  href: string;
  property_id?: string;
};

const SETTINGS_SLUG = 'primary';
export async function getSiteSettings({
  signal,
}: {
  signal?: AbortSignal;
} = {}): Promise<SiteSettings> {
  try {
    const builder = supabase.from('site_settings').select('*').eq('slug', SETTINGS_SLUG);

    const response: PostgrestSingleResponse<SiteSettingsRow | null> = signal
      ? await builder.abortSignal(signal).maybeSingle()
      : await builder.maybeSingle();

    if (response.error !== null) {
      throw response.error;
    }

    const record = response.data ?? null;
    if (record === null) {
      throw createNotFoundError('Site settings not found.');
    }

    return mapRowToSiteSettings(record);
  } catch (error) {
    return handleSupabaseError(error);
  }
}

export async function updateSiteSettings({
  values,
  signal,
}: {
  values: SiteSettingsUpdateInput;
  signal?: AbortSignal;
}): Promise<SiteSettings> {
  try {
    const payload = mapUpdateInputToPayload(values);

    const builder = supabase
      .from('site_settings')
      .update(payload)
      .eq('slug', SETTINGS_SLUG)
      .select('*');

    const response: PostgrestSingleResponse<SiteSettingsRow | null> = signal
      ? await builder.abortSignal(signal).maybeSingle()
      : await builder.maybeSingle();

    if (response.error !== null) {
      throw response.error;
    }

    const record = response.data ?? null;
    if (record === null) {
      throw createNotFoundError('Site settings not found.');
    }

    return mapRowToSiteSettings(record);
  } catch (error) {
    return handleSupabaseError(error);
  }
}

function mapRowToSiteSettings(row: SiteSettingsRow): SiteSettings {
  return {
    id: row.id,
    slug: row.slug,
    bookingEmail: row.booking_email,
    contactEmail: row.contact_email,
    contactPhone: row.contact_phone,
    contactAddressLines: Array.isArray(row.contact_address_lines)
      ? row.contact_address_lines.filter((value) => typeof value === 'string')
      : [],
    contactHours: parseContactHours(row.contact_hours),
    leadershipContacts: parseLeadershipContacts(row.leadership_contacts),
    mapEmbedUrl: row.map_embed_url,
    brand: parseBrandContent(row.brand_content),
    navigation: parseNavigationContent(row.navigation_content),
    footer: parseFooterContent(row.footer_content),
    seo: parseSeoContent(row.seo_content),
    home: parseHomeContent(row.home_content),
    leadCapture: parseLeadCaptureContent(row.lead_capture_content),
    about: parseAboutContent(row.about_content),
    publicContent: parsePublicContent(row.public_content),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapUpdateInputToPayload(values: SiteSettingsUpdateInput): SiteSettingsUpdate {
  return {
    booking_email: values.bookingEmail.trim(),
    contact_email: values.contactEmail.trim(),
    contact_phone: values.contactPhone.trim(),
    contact_address_lines: values.contactAddressLines
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0),
    contact_hours: serializeContactHours(values.contactHours),
    leadership_contacts: serializeLeadershipContacts(values.leadershipContacts),
    map_embed_url: values.mapEmbedUrl.trim(),
    brand_content: serializeBrandContent(values.brand),
    navigation_content: serializeNavigationContent(values.navigation),
    footer_content: serializeFooterContent(values.footer),
    seo_content: serializeSeoContent(values.seo),
    home_content: serializeHomeContent(values.home),
    lead_capture_content: serializeLeadCapture(values.leadCapture),
    about_content: serializeAboutContent(values.about),
    public_content: serializePublicContent(values.publicContent),
  } satisfies SiteSettingsUpdate;
}

function parseString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeFloorCopy(value: string): string {
  if (value.length === 0) {
    return value;
  }

  const withFloorToken = value.replace(/\{level\}/gi, '{floor}');
  return withFloorToken.replace(/\blevels?\b/gi, (match) => {
    const lower = match.toLowerCase();
    const replacement = lower === 'levels' ? 'floors' : 'floor';
    if (match === match.toUpperCase()) {
      return replacement.toUpperCase();
    }
    if (match === lower) {
      return replacement;
    }
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  });
}

function normalizeFloorValueTemplate(value: string): string {
  if (value.length === 0) {
    return value;
  }

  const withFloorToken = value.replace(/\{level\}/gi, '{floor}');
  const stripped = withFloorToken
    .replace(/\b(level|floor)s?\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (stripped.length === 0 || !/\{\w+\}/.test(stripped)) {
    return '{floor}';
  }

  return stripped;
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string');
}

function parseTitleDescriptionList(value: unknown): Array<{ title: string; description: string }> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (!isJsonObject(entry)) {
        return null;
      }
      const title = parseString(entry.title);
      const description = parseString(entry.description);
      if (title.length === 0 && description.length === 0) {
        return null;
      }
      return { title, description };
    })
    .filter((entry): entry is { title: string; description: string } => entry !== null);
}

function parseLabelValueList(value: unknown): Array<{ label: string; value: string }> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (!isJsonObject(entry)) {
        return null;
      }
      const label = parseString(entry.label);
      const valueText = parseString(entry.value);
      if (label.length === 0 && valueText.length === 0) {
        return null;
      }
      return { label, value: valueText };
    })
    .filter((entry): entry is { label: string; value: string } => entry !== null);
}

function parseValueLabelList(value: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (!isJsonObject(entry)) {
        return null;
      }
      const valueText = parseString(entry.value);
      const label = parseString(entry.label);
      if (valueText.length === 0 && label.length === 0) {
        return null;
      }
      return { value: valueText, label };
    })
    .filter((entry): entry is { value: string; label: string } => entry !== null);
}

function parseBrandContent(value: unknown): BrandContent {
  const content = isJsonObject(value) ? value : {};

  return {
    siteName: typeof content.site_name === 'string' ? content.site_name : '',
    tagline: typeof content.tagline === 'string' ? content.tagline : '',
    logoUrl: typeof content.logo_url === 'string' ? content.logo_url : null,
    faviconUrl: typeof content.favicon_url === 'string' ? content.favicon_url : null,
    defaultOgImageUrl:
      typeof content.default_og_image_url === 'string' ? content.default_og_image_url : null,
  } satisfies BrandContent;
}

function parseNavigationShortcut(value: unknown): NavigationShortcut {
  const content = isJsonObject(value) ? value : {};

  return {
    label: typeof content.label === 'string' ? content.label : '',
    href: typeof content.href === 'string' ? content.href : '',
  } satisfies NavigationShortcut;
}

function parseNavigationAction(value: unknown): NavigationLinkAction {
  if (value === 'call' || value === 'auth') {
    return value;
  }
  return 'link';
}

function parseNavigationStyle(value: unknown): NavigationLinkStyle {
  if (value === 'button' || value === 'outline') {
    return value;
  }
  return 'link';
}

function parseNavigationChildLinks(value: unknown): NavigationChildLink[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!isJsonObject(entry)) {
        return null;
      }

      const propertyId = typeof entry.property_id === 'string' ? entry.property_id : '';
      const label = typeof entry.label === 'string' ? entry.label : '';
      const href = typeof entry.href === 'string' ? entry.href : '';
      const enabled = typeof entry.enabled === 'boolean' ? entry.enabled : true;
      const external = typeof entry.external === 'boolean' ? entry.external : false;
      const order =
        typeof entry.order === 'number' && Number.isFinite(entry.order) ? entry.order : index + 1;

      const hasProperty = propertyId.length > 0;
      if (!hasProperty && (label.length === 0 || href.length === 0)) {
        return null;
      }

      return {
        label,
        href,
        enabled,
        external,
        order,
        ...(hasProperty ? { propertyId } : {}),
      };
    })
    .filter((entry): entry is NavigationChildLink => entry !== null);
}

function parseNavigationContent(value: unknown): NavigationContent {
  const content = isJsonObject(value) ? value : {};
  const linksSource = Array.isArray(content.links) ? content.links : [];
  const legacyPropertiesLinks = parseNavigationChildLinks(content.properties_links);
  const legacyPropertiesLabel =
    typeof content.properties_label === 'string' ? content.properties_label : '';

  const links = linksSource
    .map((entry, index) => {
      if (!isJsonObject(entry)) {
        return null;
      }

      const label = typeof entry.label === 'string' ? entry.label : '';
      const href = typeof entry.href === 'string' ? entry.href : '';
      const enabled = typeof entry.enabled === 'boolean' ? entry.enabled : true;
      const external = typeof entry.external === 'boolean' ? entry.external : false;
      const order =
        typeof entry.order === 'number' && Number.isFinite(entry.order) ? entry.order : index + 1;
      const action = parseNavigationAction(entry.action);
      const style = parseNavigationStyle(entry.style);
      const children = parseNavigationChildLinks(entry.children);
      const requiresHref = children.length === 0 && action === 'link';

      if (label.length === 0 || (requiresHref && href.length === 0)) {
        return null;
      }

      return {
        label,
        href,
        enabled,
        external,
        order,
        action,
        style,
        ...(children.length > 0 ? { children } : {}),
      };
    })
    .filter((entry): entry is NavigationLinkWithDefaults => entry !== null);

  if (legacyPropertiesLinks.length > 0) {
    const resolvedLabel =
      legacyPropertiesLabel.trim().length > 0 ? legacyPropertiesLabel.trim() : 'Properties';
    const existingIndex = links.findIndex(
      (entry) => entry.label.trim().toLowerCase() === resolvedLabel.toLowerCase(),
    );

    if (existingIndex >= 0) {
      const existing = links[existingIndex];
      if (!existing.children || existing.children.length === 0) {
        links[existingIndex] = {
          ...existing,
          href: '',
          external: false,
          action: 'link',
          style: 'link',
          children: legacyPropertiesLinks,
        };
      }
    } else {
      links.unshift({
        label: resolvedLabel,
        href: '',
        enabled: true,
        external: false,
        order: 0,
        action: 'link',
        style: 'link',
        children: legacyPropertiesLinks,
      });
    }
  }

  return {
    links,
    mobileTitle: typeof content.mobile_title === 'string' ? content.mobile_title : '',
    mobileDescription:
      typeof content.mobile_description === 'string' ? content.mobile_description : '',
    mobileShortcutHeading:
      typeof content.mobile_shortcut_heading === 'string' ? content.mobile_shortcut_heading : '',
    mobileShortcutDescription:
      typeof content.mobile_shortcut_description === 'string'
        ? content.mobile_shortcut_description
        : '',
    mobileShortcutPrimary: parseNavigationShortcut(content.mobile_shortcut_primary),
    mobileShortcutSecondary: parseNavigationShortcut(content.mobile_shortcut_secondary),
  } satisfies NavigationContent;
}

function parseFooterContent(value: unknown): FooterContent {
  const content = isJsonObject(value) ? value : {};

  return {
    companyName: typeof content.company_name === 'string' ? content.company_name : '',
    copyrightText: typeof content.copyright_text === 'string' ? content.copyright_text : '',
  } satisfies FooterContent;
}

function parseSeoContent(value: unknown): SeoContent {
  const content = isJsonObject(value) ? value : {};

  return {
    defaultDescription:
      typeof content.default_description === 'string' ? content.default_description : '',
    canonicalOrigin: typeof content.canonical_origin === 'string' ? content.canonical_origin : '',
  } satisfies SeoContent;
}

function parseContactHours(value: unknown): ContactHour[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => (isJsonObject(entry) ? entry : null))
    .filter((entry): entry is Record<string, unknown> => entry !== null)
    .map((entry) => ({
      label: typeof entry.label === 'string' ? entry.label : '',
      value: typeof entry.value === 'string' ? entry.value : '',
    }))
    .filter((entry) => entry.label.length > 0 || entry.value.length > 0);
}

function parseLeadershipContacts(value: unknown): LeadershipContact[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => (isJsonObject(entry) ? entry : null))
    .filter((entry): entry is Record<string, unknown> => entry !== null)
    .map((entry) => ({
      name: typeof entry.name === 'string' ? entry.name : '',
      title: typeof entry.title === 'string' ? entry.title : null,
    }))
    .filter((entry) => entry.name.length > 0 || (entry.title ?? '').length > 0);
}

function parseHomeHeroMediaType(value: unknown): HomeHeroMediaType {
  return value === 'image' ? 'image' : 'video';
}

function parseHomeContent(value: unknown): HomeContent {
  const content = isJsonObject(value) ? value : {};
  const hero = isJsonObject(content.hero) ? content.hero : {};
  const availability = isJsonObject(content.availability) ? content.availability : {};
  const contact = isJsonObject(content.contact) ? content.contact : {};
  const seo = isJsonObject(content.seo) ? content.seo : {};
  const heroMedia = isJsonObject(content.hero_media) ? content.hero_media : {};
  const availabilityMessages = isJsonObject(content.availability_messages)
    ? content.availability_messages
    : {};
  const heroMediaType = parseHomeHeroMediaType(heroMedia.media_type);

  return {
    hero: {
      eyebrow: parseString(hero.eyebrow),
      title: parseString(hero.title),
      description: parseString(hero.description),
    },
    availability: {
      eyebrow: parseString(availability.eyebrow),
      title: parseString(availability.title),
      description: parseString(availability.description),
      ctaLabel: parseString(availability.cta_label),
      ctaHref: parseString(availability.cta_href),
    },
    contact: {
      heading: parseString(contact.heading),
      description: parseString(contact.description),
    },
    seo: {
      title: parseString(seo.title),
      description: parseString(seo.description),
    },
    heroMedia: {
      label: parseString(heroMedia.label),
      location: parseString(heroMedia.location),
      ariaLabel: parseString(heroMedia.aria_label),
      url: parseString(heroMedia.url),
      mediaType: heroMediaType,
    },
    availabilityMessages: {
      emptyMessage: parseString(availabilityMessages.empty_message),
      errorMessage: parseString(availabilityMessages.error_message),
    },
  } satisfies HomeContent;
}

function parseLeadCaptureContent(value: unknown): LeadCaptureContent {
  const content = isJsonObject(value) ? value : {};
  const seo = isJsonObject(content.seo) ? content.seo : {};
  const form = isJsonObject(content.form) ? content.form : {};

  return {
    eyebrow: parseString(content.eyebrow),
    title: parseString(content.title),
    description: parseString(content.description),
    thankYou: parseString(content.thank_you),
    seo: {
      title: parseString(seo.title),
      description: parseString(seo.description),
    },
    thankYouTitle: parseString(content.thank_you_title),
    benefitsHeading: parseString(content.benefits_heading),
    benefits: parseTitleDescriptionList(content.benefits) as LeadBenefit[],
    stats: parseValueLabelList(content.stats) as LeadStat[],
    form: {
      fullNameLabel: parseString(form.full_name_label),
      emailLabel: parseString(form.email_label),
      phoneLabel: parseString(form.phone_label),
      budgetLabel: parseString(form.budget_label),
      moveInLabel: parseString(form.move_in_label),
      notesLabel: parseString(form.notes_label),
      submitLabel: parseString(form.submit_label),
      consentText: parseString(form.consent_text),
      successToast: parseString(form.success_toast),
      errorToast: parseString(form.error_toast),
    } satisfies LeadFormContent,
  } satisfies LeadCaptureContent;
}

function parseAboutContent(value: unknown): AboutContent {
  const content = isJsonObject(value) ? value : {};
  const seo = isJsonObject(content.seo) ? content.seo : {};
  const values = isJsonObject(content.values) ? content.values : {};
  const portfolio = isJsonObject(content.portfolio) ? content.portfolio : {};

  return {
    heroTitle: parseString(content.hero_title),
    heroSubtitle: parseString(content.hero_subtitle),
    tagline: parseString(content.tagline),
    heroPrimaryCtaLabel: parseString(content.hero_primary_cta_label),
    heroPrimaryCtaHref: parseString(content.hero_primary_cta_href),
    heroSecondaryCtaLabel: parseString(content.hero_secondary_cta_label),
    heroSecondaryCtaHref: parseString(content.hero_secondary_cta_href),
    highlightTitle: parseString(content.highlight_title),
    highlightDescription:
      typeof content.highlight_description === 'string' ? content.highlight_description : '',
    featuredProperties: parseStringArray(content.featured_properties),
    snapshotEyebrow: parseString(content.snapshot_eyebrow),
    snapshotStats: parseLabelValueList(content.snapshot_stats) as AboutPortfolioStat[],
    bodyHeading: parseString(content.body_heading),
    bodyDescription: parseString(content.body_description),
    paragraphs: parseStringArray(content.paragraphs),
    detailsSummaryLabel: parseString(content.details_summary_label),
    promiseLabel: parseString(content.promise_label),
    seo: {
      title: parseString(seo.title),
      description: parseString(seo.description),
    },
    values: {
      title: parseString(values.title),
      description: parseString(values.description),
      ctaLabel: parseString(values.cta_label),
      ctaHref: parseString(values.cta_href),
      pillars: parseTitleDescriptionList(values.pillars) as AboutValuePillar[],
    } satisfies AboutValuesContent,
    portfolio: {
      title: parseString(portfolio.title),
      description: parseString(portfolio.description),
      ctaLabel: parseString(portfolio.cta_label),
      ctaHref: parseString(portfolio.cta_href),
      flagshipEyebrow: parseString(portfolio.flagship_eyebrow),
      flagshipTitle: parseString(portfolio.flagship_title),
      flagshipDescription: parseString(portfolio.flagship_description),
      flagshipStats: parseLabelValueList(portfolio.flagship_stats) as AboutPortfolioStat[],
      flagshipFeaturesHeading: parseString(portfolio.flagship_features_heading),
      flagshipFeaturesToggleLabel: parseString(portfolio.flagship_features_toggle_label),
      flagshipFeatures: parseStringArray(portfolio.flagship_features),
      downtownEyebrow: parseString(portfolio.downtown_eyebrow),
      downtownTitle: parseString(portfolio.downtown_title),
      downtownDescription: parseString(portfolio.downtown_description),
      downtownIdealHeading: parseString(portfolio.downtown_ideal_heading),
      downtownTenants: parseStringArray(portfolio.downtown_tenants),
      downtownAddressesHeading: parseString(portfolio.downtown_addresses_heading),
      retailEyebrow: parseString(portfolio.retail_eyebrow),
      retailTitle: parseString(portfolio.retail_title),
      retailDescription: parseString(portfolio.retail_description),
      retailPlazas: parseStringArray(portfolio.retail_plazas),
      retailFooter: parseString(portfolio.retail_footer),
      retailFooterLinkLabel: parseString(portfolio.retail_footer_link_label),
    } satisfies AboutPortfolioContent,
    contactHeading: parseString(content.contact_heading),
    contactDescription: parseString(content.contact_description),
  } satisfies AboutContent;
}

function parseContactPageContent(value: unknown): ContactPageContent {
  const content = isJsonObject(value) ? value : {};
  const seo = isJsonObject(content.seo) ? content.seo : {};
  const hero = isJsonObject(content.hero) ? content.hero : {};
  const heroCard = isJsonObject(content.hero_card) ? content.hero_card : {};
  const body = isJsonObject(content.body) ? content.body : {};
  const details = isJsonObject(content.details) ? content.details : {};
  const form = isJsonObject(content.form) ? content.form : {};
  const fields = isJsonObject(form.fields) ? form.fields : {};

  return {
    seo: {
      title: parseString(seo.title),
      description: parseString(seo.description),
    },
    hero: {
      eyebrow: parseString(hero.eyebrow),
      title: parseString(hero.title),
      description: parseString(hero.description),
      responseTime: parseString(hero.response_time),
    },
    heroCard: {
      eyebrow: parseString(heroCard.eyebrow),
      title: parseString(heroCard.title),
      description: parseString(heroCard.description),
      callLabel: parseString(heroCard.call_label),
      emailLabel: parseString(heroCard.email_label),
      mapLabel: parseString(heroCard.map_label),
    },
    body: {
      title: parseString(body.title),
      description: parseString(body.description),
      highlights: parseStringArray(body.highlights),
      responseTimeLabel: parseString(body.response_time_label),
      responseTimeValue: parseString(body.response_time_value),
      directionsLabel: parseString(body.directions_label),
      availabilitiesLabel: parseString(body.availabilities_label),
    },
    details: {
      title: parseString(details.title),
      description: parseString(details.description),
    },
    form: {
      title: parseString(form.title),
      description: parseString(form.description),
      successMessage: parseString(form.success_message),
      errorMessage: parseString(form.error_message),
      helperText: parseString(form.helper_text),
      submitLabel: parseString(form.submit_label),
      fields: {
        fullNameLabel: parseString(fields.full_name_label),
        phoneLabel: parseString(fields.phone_label),
        emailLabel: parseString(fields.email_label),
        messageLabel: parseString(fields.message_label),
        fullNamePlaceholder: parseString(fields.full_name_placeholder),
        phonePlaceholder: parseString(fields.phone_placeholder),
        emailPlaceholder: parseString(fields.email_placeholder),
        messagePlaceholder: parseString(fields.message_placeholder),
        spaceTypeLabel: parseString(fields.space_type_label),
        spaceTypePlaceholder: parseString(fields.space_type_placeholder),
        spaceTypeOptions: parseStringArray(fields.space_type_options),
        targetSizeLabel: parseString(fields.target_size_label),
        targetSizePlaceholder: parseString(fields.target_size_placeholder),
        timelineLabel: parseString(fields.timeline_label),
        timelinePlaceholder: parseString(fields.timeline_placeholder),
        timelineOptions: parseStringArray(fields.timeline_options),
      },
    },
  } satisfies ContactPageContent;
}

function parseAvailabilitiesPageContent(value: unknown): AvailabilitiesPageContent {
  const content = isJsonObject(value) ? value : {};
  const seo = isJsonObject(content.seo) ? content.seo : {};
  const heading = isJsonObject(content.heading) ? content.heading : {};
  const filters = isJsonObject(content.filters) ? content.filters : {};
  const viewToggle = isJsonObject(content.view_toggle) ? content.view_toggle : {};
  const filterControls = isJsonObject(content.filter_controls) ? content.filter_controls : {};
  const results = isJsonObject(content.results) ? content.results : {};
  const list = isJsonObject(content.list) ? content.list : {};
  const mapPanel = isJsonObject(content.map_panel) ? content.map_panel : {};
  const mapUnitRow = isJsonObject(content.map_unit_row) ? content.map_unit_row : {};
  const quickView = isJsonObject(content.quick_view) ? content.quick_view : {};

  return {
    seo: {
      title: parseString(seo.title),
      description: parseString(seo.description),
    },
    heading: {
      eyebrow: parseString(heading.eyebrow),
      title: parseString(heading.title),
      description: parseString(heading.description),
    },
    filters: {
      title: parseString(filters.title),
      subtitle: parseString(filters.subtitle),
      emptyLabel: parseString(filters.empty_label),
      activeTemplate: parseString(filters.active_template),
      resetLabel: parseString(filters.reset_label),
      openLabel: parseString(filters.open_label),
      drawerTitle: parseString(filters.drawer_title),
      drawerApplyLabel: parseString(filters.drawer_apply_label),
      drawerResetLabel: parseString(filters.drawer_reset_label),
      chipPriceLabel: parseString(filters.chip_price_label),
      chipSizeLabel: parseString(filters.chip_size_label),
      chipUseLabel: parseString(filters.chip_use_label),
    },
    viewToggle: {
      label: parseString(viewToggle.label),
      cardsLabel: parseString(viewToggle.cards_label),
      mapLabel: parseString(viewToggle.map_label),
    },
    filterControls: {
      priceTitle: parseString(filterControls.price_title),
      priceDescription: parseString(filterControls.price_description),
      sizeTitle: parseString(filterControls.size_title),
      sizeDescription: parseString(filterControls.size_description),
      usesTitle: parseString(filterControls.uses_title),
      usesDescription: parseString(filterControls.uses_description),
      minLabel: parseString(filterControls.min_label),
      maxLabel: parseString(filterControls.max_label),
      placeholder: parseString(filterControls.placeholder),
    },
    results: {
      noMatchesMessage: parseString(results.no_matches_message),
      noAvailabilityMessage: parseString(results.no_availability_message),
      clearFiltersLabel: parseString(results.clear_filters_label),
      errorMessage: parseString(results.error_message),
    },
    list: {
      addressEyebrow: parseString(list.address_eyebrow),
      unitCountSingular: parseString(list.unit_count_singular),
      unitCountPlural: parseString(list.unit_count_plural),
      viewPropertyLabel: parseString(list.view_property_label),
    },
    mapPanel: {
      backLabel: parseString(mapPanel.back_label),
      addressEyebrow: parseString(mapPanel.address_eyebrow),
      addressesEyebrow: parseString(mapPanel.addresses_eyebrow),
      browseTitle: parseString(mapPanel.browse_title),
      browseDescription: parseString(mapPanel.browse_description),
      selectUnitEyebrow: parseString(mapPanel.select_unit_eyebrow),
      selectUnitDescription: parseString(mapPanel.select_unit_description),
      tapPinEyebrow: parseString(mapPanel.tap_pin_eyebrow),
      tapPinDescription: parseString(mapPanel.tap_pin_description),
      suiteCountSingular: parseString(mapPanel.suite_count_singular),
      suiteCountPlural: parseString(mapPanel.suite_count_plural),
      mapPinFallback: parseString(mapPanel.map_pin_fallback),
    },
    mapUnitRow: {
      unitLabelTemplate: parseString(mapUnitRow.unit_label_template),
      levelLabelTemplate: normalizeFloorValueTemplate(parseString(mapUnitRow.level_label_template)),
      divisibleLabel: parseString(mapUnitRow.divisible_label),
      incentivesLabel: parseString(mapUnitRow.incentives_label),
    },
    quickView: {
      eyebrow: parseString(quickView.eyebrow),
      titleTemplate: normalizeFloorCopy(parseString(quickView.title_template)),
      rentLabel: parseString(quickView.rent_label),
      rentSuffix: parseString(quickView.rent_suffix),
      sizeLabel: parseString(quickView.size_label),
      availabilityLabel: parseString(quickView.availability_label),
      scheduleLabel: parseString(quickView.schedule_label),
      detailsLabel: parseString(quickView.details_label),
      divisibleLabel: parseString(quickView.divisible_label),
      incentivesLabel: parseString(quickView.incentives_label),
    },
  } satisfies AvailabilitiesPageContent;
}

function parsePropertyDetailContent(value: unknown): PropertyDetailContent {
  const content = isJsonObject(value) ? value : {};
  const messages = isJsonObject(content.messages) ? content.messages : {};
  const navigation = isJsonObject(content.navigation) ? content.navigation : {};
  const availabilityStrip = isJsonObject(content.availability_strip)
    ? content.availability_strip
    : {};
  const overview = isJsonObject(content.overview) ? content.overview : {};
  const units = isJsonObject(content.units) ? content.units : {};
  const unitsTable = isJsonObject(content.units_table) ? content.units_table : {};
  const unitFilters = isJsonObject(content.unit_filters) ? content.unit_filters : {};
  const highlights = isJsonObject(content.highlights) ? content.highlights : {};
  const location = isJsonObject(content.location) ? content.location : {};
  const keyFigures = isJsonObject(content.key_figures) ? content.key_figures : {};
  const labels = isJsonObject(content.labels) ? content.labels : {};

  return {
    messages: {
      unpublishedMessage: parseString(messages.unpublished_message),
      ctaLabel: parseString(messages.cta_label),
    },
    navigation: {
      overviewLabel: parseString(navigation.overview_label),
      highlightsLabel: parseString(navigation.highlights_label),
      locationLabel: parseString(navigation.location_label),
      availabilityLabel: parseString(navigation.availability_label),
    },
    availabilityStrip: {
      noUnitsLabel: parseString(availabilityStrip.no_units_label),
      availableUnitsTemplate: parseString(availabilityStrip.available_units_template),
      updatedLabel: parseString(availabilityStrip.updated_label),
    },
    overview: {
      eyebrow: parseString(overview.eyebrow),
      viewOnMapLabel: parseString(overview.view_on_map_label),
      titleTemplate: parseString(overview.title_template),
    },
    units: {
      eyebrow: parseString(units.eyebrow),
      title: parseString(units.title),
      listedTemplate: parseString(units.listed_template),
      emptyAllMessage: parseString(units.empty_all_message),
      emptyFilteredMessage: parseString(units.empty_filtered_message),
      unitCountSingular: parseString(units.unit_count_singular),
      unitCountPlural: parseString(units.unit_count_plural),
      floorPlanFallback: parseString(units.floor_plan_fallback),
      monthlyLeaseLabel: parseString(units.monthly_lease_label),
      contactLabel: parseString(units.contact_label),
      contactFallback: parseString(units.contact_fallback),
      bookButtonLabel: parseString(units.book_button_label),
      viewDetailsLabel: parseString(units.view_details_label),
    },
    unitsTable: {
      suiteHeader: parseString(unitsTable.suite_header),
      floorHeader: normalizeFloorCopy(parseString(unitsTable.floor_header)),
      sizeHeader: parseString(unitsTable.size_header),
      monthlyLeaseHeader: parseString(unitsTable.monthly_lease_header),
      rateHeader: parseString(unitsTable.rate_header),
      actionHeader: parseString(unitsTable.action_header),
      noUnitsMessage: parseString(unitsTable.no_units_message),
      noMatchesMessage: parseString(unitsTable.no_matches_message),
    },
    unitFilters: {
      sizeLabel: parseString(unitFilters.size_label),
      floorLabel: normalizeFloorCopy(parseString(unitFilters.floor_label)),
      rateLabel: parseString(unitFilters.rate_label),
      sizeAllLabel: parseString(unitFilters.size_all_label),
      floorAllLabel: normalizeFloorCopy(parseString(unitFilters.floor_all_label)),
      rateAllLabel: parseString(unitFilters.rate_all_label),
    },
    highlights: {
      eyebrow: parseString(highlights.eyebrow),
      title: parseString(highlights.title),
      description: parseString(highlights.description),
      divisibleLabel: parseString(highlights.divisible_label),
      incentivesLabel: parseString(highlights.incentives_label),
      floorPlanLabel: parseString(highlights.floor_plan_label),
      emptyMessage: parseString(highlights.empty_message),
    },
    location: {
      title: parseString(location.title),
      directionsLabel: parseString(location.directions_label),
    },
    keyFigures: {
      title: parseString(keyFigures.title),
      subtitle: parseString(keyFigures.subtitle),
      totalRentableLabel: parseString(keyFigures.total_rentable_label),
      unitsLiveLabel: parseString(keyFigures.units_live_label),
      incentivesLabel: parseString(keyFigures.incentives_label),
      divisibleLabel: parseString(keyFigures.divisible_label),
    },
    labels: {
      rentFallback: parseString(labels.rent_fallback),
      availabilityRangeFallback: parseString(labels.availability_range_fallback),
      availabilitySingleFallback: parseString(labels.availability_single_fallback),
      unitAvailabilityImmediateLabel: parseString(labels.unit_availability_immediate_label),
      unitAvailabilityNegotiableLabel: parseString(labels.unit_availability_negotiable_label),
    },
  } satisfies PropertyDetailContent;
}

function parseUnitDetailContent(value: unknown): UnitDetailContent {
  const content = isJsonObject(value) ? value : {};
  const messages = isJsonObject(content.messages) ? content.messages : {};
  const actions = isJsonObject(content.actions) ? content.actions : {};
  const headings = isJsonObject(content.headings) ? content.headings : {};
  const labels = isJsonObject(content.labels) ? content.labels : {};
  const leasingPrompts = isJsonObject(content.leasing_prompts) ? content.leasing_prompts : {};

  return {
    messages: {
      notFoundMessage: parseString(messages.not_found_message),
      unavailableMessage: parseString(messages.unavailable_message),
    },
    actions: {
      viewPropertyLabel: parseString(actions.view_property_label),
      browseAvailabilitiesLabel: parseString(actions.browse_availabilities_label),
      viewOtherUnitsLabel: parseString(actions.view_other_units_label),
      stickyEyebrow: parseString(actions.sticky_eyebrow),
      stickyButtonLabel: parseString(actions.sticky_button_label),
    },
    headings: {
      atAGlanceTitle: parseString(headings.at_a_glance_title),
      propertyDetailsTitle: parseString(headings.property_details_title),
      leaseDetailsTitle: parseString(headings.lease_details_title),
      propertyDescriptionEyebrow: parseString(headings.property_description_eyebrow),
      propertyDescriptionTitle: parseString(headings.property_description_title),
      scheduleEyebrow: parseString(headings.schedule_eyebrow),
      scheduleTitle: parseString(headings.schedule_title),
      leasingDetailsEyebrow: parseString(headings.leasing_details_eyebrow),
      leasingDetailsTitle: parseString(headings.leasing_details_title),
      photoGalleryTitle: parseString(headings.photo_gallery_title),
    },
    labels: {
      sizeLabel: parseString(labels.size_label),
      floorLabel: normalizeFloorCopy(parseString(labels.floor_label)),
      divisibleLabel: parseString(labels.divisible_label),
      statusLabel: parseString(labels.status_label),
      availabilityLabel: parseString(labels.availability_label),
      possessionLabel: parseString(labels.possession_label),
      updatedLabel: parseString(labels.updated_label),
      immediateLabel: parseString(labels.immediate_label),
      negotiableLabel: parseString(labels.negotiable_label),
      immediatePossessionLabel: parseString(labels.immediate_possession_label),
      negotiablePossessionLabel: parseString(labels.negotiable_possession_label),
      updatedRecentlyLabel: parseString(labels.updated_recently_label),
    },
    leasingPrompts: {
      industrial: parseString(leasingPrompts.industrial),
      flex: parseString(leasingPrompts.flex),
      retail: parseString(leasingPrompts.retail),
      office: parseString(leasingPrompts.office),
      fallback: parseString(leasingPrompts.fallback),
    },
    photoGalleryEmptyMessage: parseString(content.photo_gallery_empty_message),
  } satisfies UnitDetailContent;
}

function parseShowingDialogContent(value: unknown): ShowingDialogContent {
  const content = isJsonObject(value) ? value : {};
  const steps = isJsonObject(content.steps) ? content.steps : {};
  const fields = isJsonObject(content.fields) ? content.fields : {};
  const fieldHints = isJsonObject(content.field_hints) ? content.field_hints : {};
  const review = isJsonObject(content.review) ? content.review : {};
  const buttons = isJsonObject(content.buttons) ? content.buttons : {};
  const errors = isJsonObject(content.errors) ? content.errors : {};
  const success = isJsonObject(content.success) ? content.success : {};

  return {
    title: parseString(content.title),
    description: parseString(content.description),
    stepIndicatorTemplate: parseString(content.step_indicator_template),
    selectedUnitLabel: parseString(content.selected_unit_label),
    selectedUnitDescription: parseString(content.selected_unit_description),
    steps: {
      contactLabel: parseString(steps.contact_label),
      scheduleLabel: parseString(steps.schedule_label),
      reviewLabel: parseString(steps.review_label),
    },
    fields: {
      firstNameLabel: parseString(fields.first_name_label),
      lastNameLabel: parseString(fields.last_name_label),
      emailLabel: parseString(fields.email_label),
      phoneLabel: parseString(fields.phone_label),
      dateLabel: parseString(fields.date_label),
      timeLabel: parseString(fields.time_label),
      messageLabel: parseString(fields.message_label),
    },
    fieldHints: {
      dateHint: parseString(fieldHints.date_hint),
      timeHint: parseString(fieldHints.time_hint),
    },
    messagePlaceholder: parseString(content.message_placeholder),
    review: {
      title: parseString(review.title),
      contactLabel: parseString(review.contact_label),
      slotLabel: parseString(review.slot_label),
      unitLabel: parseString(review.unit_label),
      messageLabel: parseString(review.message_label),
      confirmationNote: parseString(review.confirmation_note),
      guidanceNext: parseString(review.guidance_next),
      guidanceReview: parseString(review.guidance_review),
    },
    buttons: {
      cancelLabel: parseString(buttons.cancel_label),
      backLabel: parseString(buttons.back_label),
      nextLabel: parseString(buttons.next_label),
      confirmLabel: parseString(buttons.confirm_label),
      doneLabel: parseString(buttons.done_label),
    },
    errors: {
      contactMissing: parseString(errors.contact_missing),
      scheduleMissing: parseString(errors.schedule_missing),
      submitError: parseString(errors.submit_error),
    },
    success: {
      title: parseString(success.title),
      description: parseString(success.description),
      timeLabel: parseString(success.time_label),
      emailLabel: parseString(success.email_label),
      nameLabel: parseString(success.name_label),
      messageLabel: parseString(success.message_label),
      calendarLabel: parseString(success.calendar_label),
      portalInviteQueued: parseString(success.portal_invite_queued),
      portalInviteSent: parseString(success.portal_invite_sent),
    },
    unavailableTooltip: parseString(content.unavailable_tooltip),
  } satisfies ShowingDialogContent;
}

function parseSharedPublicContent(value: unknown): SharedPublicContent {
  const content = isJsonObject(value) ? value : {};
  const contactSection = isJsonObject(content.contact_section) ? content.contact_section : {};
  const availabilityAlerts = isJsonObject(content.availability_alerts)
    ? content.availability_alerts
    : {};
  const propertyCard = isJsonObject(content.property_card) ? content.property_card : {};
  const unitCard = isJsonObject(content.unit_card) ? content.unit_card : {};
  const emptyStates = isJsonObject(content.empty_states) ? content.empty_states : {};

  return {
    contactSection: {
      addressLabel: parseString(contactSection.address_label),
      phoneLabel: parseString(contactSection.phone_label),
      emailLabel: parseString(contactSection.email_label),
      hoursLabel: parseString(contactSection.hours_label),
      leadershipLabel: parseString(contactSection.leadership_label),
      mapTitle: parseString(contactSection.map_title),
      mapPreviewTitle: parseString(contactSection.map_preview_title),
      mapPreviewDescription: parseString(contactSection.map_preview_description),
      mapFooter: parseString(contactSection.map_footer),
      mapLinkLabel: parseString(contactSection.map_link_label),
    },
    availabilityAlerts: {
      eyebrow: parseString(availabilityAlerts.eyebrow),
      title: parseString(availabilityAlerts.title),
      description: parseString(availabilityAlerts.description),
      emailLabel: parseString(availabilityAlerts.email_label),
      emailPlaceholder: parseString(availabilityAlerts.email_placeholder),
      submitLabel: parseString(availabilityAlerts.submit_label),
      successMessage: parseString(availabilityAlerts.success_message),
      errorMessage: parseString(availabilityAlerts.error_message),
      note: parseString(availabilityAlerts.note),
    },
    propertyCard: {
      photoFallback: parseString(propertyCard.photo_fallback),
      rentFallback: parseString(propertyCard.rent_fallback),
      availabilityFallback: parseString(propertyCard.availability_fallback),
      divisibleLabel: parseString(propertyCard.divisible_label),
      vacancyLabel: parseString(propertyCard.vacancy_label),
      vacancyAvailableLabel: parseString(propertyCard.vacancy_available_label),
      vacancyLeasedLabel: parseString(propertyCard.vacancy_leased_label),
      sizeLabel: parseString(propertyCard.size_label),
      unitCountZeroLabel: parseString(propertyCard.unit_count_zero_label),
      unitCountSingularLabel: parseString(propertyCard.unit_count_singular_label),
      unitCountPluralLabel: parseString(propertyCard.unit_count_plural_label),
    },
    unitCard: {
      photoFallback: parseString(unitCard.photo_fallback),
      availabilityLabel: parseString(unitCard.availability_label),
      availabilityFallback: parseString(unitCard.availability_fallback),
      floorLabel: normalizeFloorCopy(parseString(unitCard.floor_label)),
      viewDetailsLabel: parseString(unitCard.view_details_label),
      divisibleLabel: parseString(unitCard.divisible_label),
    },
    emptyStates: {
      propertyGridEmpty: parseString(emptyStates.property_grid_empty),
      unitGroupEmpty: parseString(emptyStates.unit_group_empty),
    },
  } satisfies SharedPublicContent;
}

function parsePublicContent(value: unknown): PublicContent {
  const content = isJsonObject(value) ? value : {};

  return {
    contactPage: parseContactPageContent(content.contact_page),
    availabilitiesPage: parseAvailabilitiesPageContent(content.availabilities_page),
    propertyDetail: parsePropertyDetailContent(content.property_detail),
    unitDetail: parseUnitDetailContent(content.unit_detail),
    showingDialog: parseShowingDialogContent(content.showing_dialog),
    shared: parseSharedPublicContent(content.shared),
  } satisfies PublicContent;
}

function serializeStringList(values: string[]): string[] {
  return values.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

function serializeTitleDescriptionList(
  values: Array<{ title: string; description: string }>,
): Json {
  return values
    .map((entry) => ({
      title: entry.title.trim(),
      description: entry.description.trim(),
    }))
    .filter((entry) => entry.title.length > 0 || entry.description.length > 0);
}

function serializeLabelValueList(values: Array<{ label: string; value: string }>): Json {
  return values
    .map((entry) => ({
      label: entry.label.trim(),
      value: entry.value.trim(),
    }))
    .filter((entry) => entry.label.length > 0 || entry.value.length > 0);
}

function serializeValueLabelList(values: Array<{ value: string; label: string }>): Json {
  return values
    .map((entry) => ({
      value: entry.value.trim(),
      label: entry.label.trim(),
    }))
    .filter((entry) => entry.value.length > 0 || entry.label.length > 0);
}

function serializeContactPageContent(content: ContactPageContent): Json {
  return {
    seo: {
      title: content.seo.title.trim(),
      description: content.seo.description.trim(),
    },
    hero: {
      eyebrow: content.hero.eyebrow.trim(),
      title: content.hero.title.trim(),
      description: content.hero.description.trim(),
      response_time: content.hero.responseTime.trim(),
    },
    hero_card: {
      eyebrow: content.heroCard.eyebrow.trim(),
      title: content.heroCard.title.trim(),
      description: content.heroCard.description.trim(),
      call_label: content.heroCard.callLabel.trim(),
      email_label: content.heroCard.emailLabel.trim(),
      map_label: content.heroCard.mapLabel.trim(),
    },
    body: {
      title: content.body.title.trim(),
      description: content.body.description.trim(),
      highlights: serializeStringList(content.body.highlights),
      response_time_label: content.body.responseTimeLabel.trim(),
      response_time_value: content.body.responseTimeValue.trim(),
      directions_label: content.body.directionsLabel.trim(),
      availabilities_label: content.body.availabilitiesLabel.trim(),
    },
    details: {
      title: content.details.title.trim(),
      description: content.details.description.trim(),
    },
    form: {
      title: content.form.title.trim(),
      description: content.form.description.trim(),
      success_message: content.form.successMessage.trim(),
      error_message: content.form.errorMessage.trim(),
      helper_text: content.form.helperText.trim(),
      submit_label: content.form.submitLabel.trim(),
      fields: {
        full_name_label: content.form.fields.fullNameLabel.trim(),
        phone_label: content.form.fields.phoneLabel.trim(),
        email_label: content.form.fields.emailLabel.trim(),
        message_label: content.form.fields.messageLabel.trim(),
        full_name_placeholder: content.form.fields.fullNamePlaceholder.trim(),
        phone_placeholder: content.form.fields.phonePlaceholder.trim(),
        email_placeholder: content.form.fields.emailPlaceholder.trim(),
        message_placeholder: content.form.fields.messagePlaceholder.trim(),
        space_type_label: content.form.fields.spaceTypeLabel.trim(),
        space_type_placeholder: content.form.fields.spaceTypePlaceholder.trim(),
        space_type_options: serializeStringList(content.form.fields.spaceTypeOptions),
        target_size_label: content.form.fields.targetSizeLabel.trim(),
        target_size_placeholder: content.form.fields.targetSizePlaceholder.trim(),
        timeline_label: content.form.fields.timelineLabel.trim(),
        timeline_placeholder: content.form.fields.timelinePlaceholder.trim(),
        timeline_options: serializeStringList(content.form.fields.timelineOptions),
      },
    },
  } satisfies Json;
}

function serializeAvailabilitiesPageContent(content: AvailabilitiesPageContent): Json {
  return {
    seo: {
      title: content.seo.title.trim(),
      description: content.seo.description.trim(),
    },
    heading: {
      eyebrow: content.heading.eyebrow.trim(),
      title: content.heading.title.trim(),
      description: content.heading.description.trim(),
    },
    filters: {
      title: content.filters.title.trim(),
      subtitle: content.filters.subtitle.trim(),
      empty_label: content.filters.emptyLabel.trim(),
      active_template: content.filters.activeTemplate.trim(),
      reset_label: content.filters.resetLabel.trim(),
      open_label: content.filters.openLabel.trim(),
      drawer_title: content.filters.drawerTitle.trim(),
      drawer_apply_label: content.filters.drawerApplyLabel.trim(),
      drawer_reset_label: content.filters.drawerResetLabel.trim(),
      chip_price_label: content.filters.chipPriceLabel.trim(),
      chip_size_label: content.filters.chipSizeLabel.trim(),
      chip_use_label: content.filters.chipUseLabel.trim(),
    },
    view_toggle: {
      label: content.viewToggle.label.trim(),
      cards_label: content.viewToggle.cardsLabel.trim(),
      map_label: content.viewToggle.mapLabel.trim(),
    },
    filter_controls: {
      price_title: content.filterControls.priceTitle.trim(),
      price_description: content.filterControls.priceDescription.trim(),
      size_title: content.filterControls.sizeTitle.trim(),
      size_description: content.filterControls.sizeDescription.trim(),
      uses_title: content.filterControls.usesTitle.trim(),
      uses_description: content.filterControls.usesDescription.trim(),
      min_label: content.filterControls.minLabel.trim(),
      max_label: content.filterControls.maxLabel.trim(),
      placeholder: content.filterControls.placeholder.trim(),
    },
    results: {
      no_matches_message: content.results.noMatchesMessage.trim(),
      no_availability_message: content.results.noAvailabilityMessage.trim(),
      clear_filters_label: content.results.clearFiltersLabel.trim(),
      error_message: content.results.errorMessage.trim(),
    },
    list: {
      address_eyebrow: content.list.addressEyebrow.trim(),
      unit_count_singular: content.list.unitCountSingular.trim(),
      unit_count_plural: content.list.unitCountPlural.trim(),
      view_property_label: content.list.viewPropertyLabel.trim(),
    },
    map_panel: {
      back_label: content.mapPanel.backLabel.trim(),
      address_eyebrow: content.mapPanel.addressEyebrow.trim(),
      addresses_eyebrow: content.mapPanel.addressesEyebrow.trim(),
      browse_title: content.mapPanel.browseTitle.trim(),
      browse_description: content.mapPanel.browseDescription.trim(),
      select_unit_eyebrow: content.mapPanel.selectUnitEyebrow.trim(),
      select_unit_description: content.mapPanel.selectUnitDescription.trim(),
      tap_pin_eyebrow: content.mapPanel.tapPinEyebrow.trim(),
      tap_pin_description: content.mapPanel.tapPinDescription.trim(),
      suite_count_singular: content.mapPanel.suiteCountSingular.trim(),
      suite_count_plural: content.mapPanel.suiteCountPlural.trim(),
      map_pin_fallback: content.mapPanel.mapPinFallback.trim(),
    },
    map_unit_row: {
      unit_label_template: content.mapUnitRow.unitLabelTemplate.trim(),
      level_label_template: normalizeFloorValueTemplate(
        content.mapUnitRow.levelLabelTemplate.trim(),
      ),
      divisible_label: content.mapUnitRow.divisibleLabel.trim(),
      incentives_label: content.mapUnitRow.incentivesLabel.trim(),
    },
    quick_view: {
      eyebrow: content.quickView.eyebrow.trim(),
      title_template: normalizeFloorCopy(content.quickView.titleTemplate.trim()),
      rent_label: content.quickView.rentLabel.trim(),
      rent_suffix: content.quickView.rentSuffix.trim(),
      size_label: content.quickView.sizeLabel.trim(),
      availability_label: content.quickView.availabilityLabel.trim(),
      schedule_label: content.quickView.scheduleLabel.trim(),
      details_label: content.quickView.detailsLabel.trim(),
      divisible_label: content.quickView.divisibleLabel.trim(),
      incentives_label: content.quickView.incentivesLabel.trim(),
    },
  } satisfies Json;
}

function serializePropertyDetailContent(content: PropertyDetailContent): Json {
  return {
    messages: {
      unpublished_message: content.messages.unpublishedMessage.trim(),
      cta_label: content.messages.ctaLabel.trim(),
    },
    navigation: {
      overview_label: content.navigation.overviewLabel.trim(),
      highlights_label: content.navigation.highlightsLabel.trim(),
      location_label: content.navigation.locationLabel.trim(),
      availability_label: content.navigation.availabilityLabel.trim(),
    },
    availability_strip: {
      no_units_label: content.availabilityStrip.noUnitsLabel.trim(),
      available_units_template: content.availabilityStrip.availableUnitsTemplate.trim(),
      updated_label: content.availabilityStrip.updatedLabel.trim(),
    },
    overview: {
      eyebrow: content.overview.eyebrow.trim(),
      view_on_map_label: content.overview.viewOnMapLabel.trim(),
      title_template: content.overview.titleTemplate.trim(),
    },
    units: {
      eyebrow: content.units.eyebrow.trim(),
      title: content.units.title.trim(),
      listed_template: content.units.listedTemplate.trim(),
      empty_all_message: content.units.emptyAllMessage.trim(),
      empty_filtered_message: content.units.emptyFilteredMessage.trim(),
      unit_count_singular: content.units.unitCountSingular.trim(),
      unit_count_plural: content.units.unitCountPlural.trim(),
      floor_plan_fallback: content.units.floorPlanFallback.trim(),
      monthly_lease_label: content.units.monthlyLeaseLabel.trim(),
      contact_label: content.units.contactLabel.trim(),
      contact_fallback: content.units.contactFallback.trim(),
      book_button_label: content.units.bookButtonLabel.trim(),
      view_details_label: content.units.viewDetailsLabel.trim(),
    },
    units_table: {
      suite_header: content.unitsTable.suiteHeader.trim(),
      floor_header: normalizeFloorCopy(content.unitsTable.floorHeader.trim()),
      size_header: content.unitsTable.sizeHeader.trim(),
      monthly_lease_header: content.unitsTable.monthlyLeaseHeader.trim(),
      rate_header: content.unitsTable.rateHeader.trim(),
      action_header: content.unitsTable.actionHeader.trim(),
      no_units_message: content.unitsTable.noUnitsMessage.trim(),
      no_matches_message: content.unitsTable.noMatchesMessage.trim(),
    },
    unit_filters: {
      size_label: content.unitFilters.sizeLabel.trim(),
      floor_label: normalizeFloorCopy(content.unitFilters.floorLabel.trim()),
      rate_label: content.unitFilters.rateLabel.trim(),
      size_all_label: content.unitFilters.sizeAllLabel.trim(),
      floor_all_label: normalizeFloorCopy(content.unitFilters.floorAllLabel.trim()),
      rate_all_label: content.unitFilters.rateAllLabel.trim(),
    },
    highlights: {
      eyebrow: content.highlights.eyebrow.trim(),
      title: content.highlights.title.trim(),
      description: content.highlights.description.trim(),
      divisible_label: content.highlights.divisibleLabel.trim(),
      incentives_label: content.highlights.incentivesLabel.trim(),
      floor_plan_label: content.highlights.floorPlanLabel.trim(),
      empty_message: content.highlights.emptyMessage.trim(),
    },
    location: {
      title: content.location.title.trim(),
      directions_label: content.location.directionsLabel.trim(),
    },
    key_figures: {
      title: content.keyFigures.title.trim(),
      subtitle: content.keyFigures.subtitle.trim(),
      total_rentable_label: content.keyFigures.totalRentableLabel.trim(),
      units_live_label: content.keyFigures.unitsLiveLabel.trim(),
      incentives_label: content.keyFigures.incentivesLabel.trim(),
      divisible_label: content.keyFigures.divisibleLabel.trim(),
    },
    labels: {
      rent_fallback: content.labels.rentFallback.trim(),
      availability_range_fallback: content.labels.availabilityRangeFallback.trim(),
      availability_single_fallback: content.labels.availabilitySingleFallback.trim(),
      unit_availability_immediate_label: content.labels.unitAvailabilityImmediateLabel.trim(),
      unit_availability_negotiable_label: content.labels.unitAvailabilityNegotiableLabel.trim(),
    },
  } satisfies Json;
}

function serializeUnitDetailContent(content: UnitDetailContent): Json {
  return {
    messages: {
      not_found_message: content.messages.notFoundMessage.trim(),
      unavailable_message: content.messages.unavailableMessage.trim(),
    },
    actions: {
      view_property_label: content.actions.viewPropertyLabel.trim(),
      browse_availabilities_label: content.actions.browseAvailabilitiesLabel.trim(),
      view_other_units_label: content.actions.viewOtherUnitsLabel.trim(),
      sticky_eyebrow: content.actions.stickyEyebrow.trim(),
      sticky_button_label: content.actions.stickyButtonLabel.trim(),
    },
    headings: {
      at_a_glance_title: content.headings.atAGlanceTitle.trim(),
      property_details_title: content.headings.propertyDetailsTitle.trim(),
      lease_details_title: content.headings.leaseDetailsTitle.trim(),
      property_description_eyebrow: content.headings.propertyDescriptionEyebrow.trim(),
      property_description_title: content.headings.propertyDescriptionTitle.trim(),
      schedule_eyebrow: content.headings.scheduleEyebrow.trim(),
      schedule_title: content.headings.scheduleTitle.trim(),
      leasing_details_eyebrow: content.headings.leasingDetailsEyebrow.trim(),
      leasing_details_title: content.headings.leasingDetailsTitle.trim(),
      photo_gallery_title: content.headings.photoGalleryTitle.trim(),
    },
    labels: {
      size_label: content.labels.sizeLabel.trim(),
      floor_label: normalizeFloorCopy(content.labels.floorLabel.trim()),
      divisible_label: content.labels.divisibleLabel.trim(),
      status_label: content.labels.statusLabel.trim(),
      availability_label: content.labels.availabilityLabel.trim(),
      possession_label: content.labels.possessionLabel.trim(),
      updated_label: content.labels.updatedLabel.trim(),
      immediate_label: content.labels.immediateLabel.trim(),
      negotiable_label: content.labels.negotiableLabel.trim(),
      immediate_possession_label: content.labels.immediatePossessionLabel.trim(),
      negotiable_possession_label: content.labels.negotiablePossessionLabel.trim(),
      updated_recently_label: content.labels.updatedRecentlyLabel.trim(),
    },
    leasing_prompts: {
      industrial: content.leasingPrompts.industrial.trim(),
      flex: content.leasingPrompts.flex.trim(),
      retail: content.leasingPrompts.retail.trim(),
      office: content.leasingPrompts.office.trim(),
      fallback: content.leasingPrompts.fallback.trim(),
    },
    photo_gallery_empty_message: content.photoGalleryEmptyMessage.trim(),
  } satisfies Json;
}

function serializeShowingDialogContent(content: ShowingDialogContent): Json {
  return {
    title: content.title.trim(),
    description: content.description.trim(),
    step_indicator_template: content.stepIndicatorTemplate.trim(),
    selected_unit_label: content.selectedUnitLabel.trim(),
    selected_unit_description: content.selectedUnitDescription.trim(),
    steps: {
      contact_label: content.steps.contactLabel.trim(),
      schedule_label: content.steps.scheduleLabel.trim(),
      review_label: content.steps.reviewLabel.trim(),
    },
    fields: {
      first_name_label: content.fields.firstNameLabel.trim(),
      last_name_label: content.fields.lastNameLabel.trim(),
      email_label: content.fields.emailLabel.trim(),
      phone_label: content.fields.phoneLabel.trim(),
      date_label: content.fields.dateLabel.trim(),
      time_label: content.fields.timeLabel.trim(),
      message_label: content.fields.messageLabel.trim(),
    },
    field_hints: {
      date_hint: content.fieldHints.dateHint.trim(),
      time_hint: content.fieldHints.timeHint.trim(),
    },
    message_placeholder: content.messagePlaceholder.trim(),
    review: {
      title: content.review.title.trim(),
      contact_label: content.review.contactLabel.trim(),
      slot_label: content.review.slotLabel.trim(),
      unit_label: content.review.unitLabel.trim(),
      message_label: content.review.messageLabel.trim(),
      confirmation_note: content.review.confirmationNote.trim(),
      guidance_next: content.review.guidanceNext.trim(),
      guidance_review: content.review.guidanceReview.trim(),
    },
    buttons: {
      cancel_label: content.buttons.cancelLabel.trim(),
      back_label: content.buttons.backLabel.trim(),
      next_label: content.buttons.nextLabel.trim(),
      confirm_label: content.buttons.confirmLabel.trim(),
      done_label: content.buttons.doneLabel.trim(),
    },
    errors: {
      contact_missing: content.errors.contactMissing.trim(),
      schedule_missing: content.errors.scheduleMissing.trim(),
      submit_error: content.errors.submitError.trim(),
    },
    success: {
      title: content.success.title.trim(),
      description: content.success.description.trim(),
      time_label: content.success.timeLabel.trim(),
      email_label: content.success.emailLabel.trim(),
      name_label: content.success.nameLabel.trim(),
      message_label: content.success.messageLabel.trim(),
      calendar_label: content.success.calendarLabel.trim(),
      portal_invite_queued: content.success.portalInviteQueued.trim(),
      portal_invite_sent: content.success.portalInviteSent.trim(),
    },
    unavailable_tooltip: content.unavailableTooltip.trim(),
  } satisfies Json;
}

function serializeSharedPublicContent(content: SharedPublicContent): Json {
  return {
    contact_section: {
      address_label: content.contactSection.addressLabel.trim(),
      phone_label: content.contactSection.phoneLabel.trim(),
      email_label: content.contactSection.emailLabel.trim(),
      hours_label: content.contactSection.hoursLabel.trim(),
      leadership_label: content.contactSection.leadershipLabel.trim(),
      map_title: content.contactSection.mapTitle.trim(),
      map_preview_title: content.contactSection.mapPreviewTitle.trim(),
      map_preview_description: content.contactSection.mapPreviewDescription.trim(),
      map_footer: content.contactSection.mapFooter.trim(),
      map_link_label: content.contactSection.mapLinkLabel.trim(),
    },
    availability_alerts: {
      eyebrow: content.availabilityAlerts.eyebrow.trim(),
      title: content.availabilityAlerts.title.trim(),
      description: content.availabilityAlerts.description.trim(),
      email_label: content.availabilityAlerts.emailLabel.trim(),
      email_placeholder: content.availabilityAlerts.emailPlaceholder.trim(),
      submit_label: content.availabilityAlerts.submitLabel.trim(),
      success_message: content.availabilityAlerts.successMessage.trim(),
      error_message: content.availabilityAlerts.errorMessage.trim(),
      note: content.availabilityAlerts.note.trim(),
    },
    property_card: {
      photo_fallback: content.propertyCard.photoFallback.trim(),
      rent_fallback: content.propertyCard.rentFallback.trim(),
      availability_fallback: content.propertyCard.availabilityFallback.trim(),
      divisible_label: content.propertyCard.divisibleLabel.trim(),
      vacancy_label: content.propertyCard.vacancyLabel.trim(),
      vacancy_available_label: content.propertyCard.vacancyAvailableLabel.trim(),
      vacancy_leased_label: content.propertyCard.vacancyLeasedLabel.trim(),
      size_label: content.propertyCard.sizeLabel.trim(),
      unit_count_zero_label: content.propertyCard.unitCountZeroLabel.trim(),
      unit_count_singular_label: content.propertyCard.unitCountSingularLabel.trim(),
      unit_count_plural_label: content.propertyCard.unitCountPluralLabel.trim(),
    },
    unit_card: {
      photo_fallback: content.unitCard.photoFallback.trim(),
      availability_label: content.unitCard.availabilityLabel.trim(),
      availability_fallback: content.unitCard.availabilityFallback.trim(),
      floor_label: normalizeFloorCopy(content.unitCard.floorLabel.trim()),
      view_details_label: content.unitCard.viewDetailsLabel.trim(),
      divisible_label: content.unitCard.divisibleLabel.trim(),
    },
    empty_states: {
      property_grid_empty: content.emptyStates.propertyGridEmpty.trim(),
      unit_group_empty: content.emptyStates.unitGroupEmpty.trim(),
    },
  } satisfies Json;
}

function serializePublicContent(content: PublicContent): Json {
  return {
    contact_page: serializeContactPageContent(content.contactPage),
    availabilities_page: serializeAvailabilitiesPageContent(content.availabilitiesPage),
    property_detail: serializePropertyDetailContent(content.propertyDetail),
    unit_detail: serializeUnitDetailContent(content.unitDetail),
    showing_dialog: serializeShowingDialogContent(content.showingDialog),
    shared: serializeSharedPublicContent(content.shared),
  } satisfies Json;
}

function serializeContactHours(entries: ContactHour[]): Json {
  return entries
    .map((entry) => ({
      label: entry.label.trim(),
      value: entry.value.trim(),
    }))
    .filter((entry) => entry.label.length > 0 || entry.value.length > 0);
}

function serializeLeadershipContacts(entries: LeadershipContact[]): Json {
  return entries
    .map((entry) => ({
      name: entry.name.trim(),
      title: typeof entry.title === 'string' && entry.title.length > 0 ? entry.title.trim() : null,
    }))
    .filter((entry) => entry.name.length > 0 || (entry.title ?? '').length > 0);
}

function serializeBrandContent(content: BrandContent): Json {
  const logoUrl = content.logoUrl?.trim() ?? '';
  const faviconUrl = content.faviconUrl?.trim() ?? '';
  const ogImageUrl = content.defaultOgImageUrl?.trim() ?? '';

  return {
    site_name: content.siteName.trim(),
    tagline: content.tagline.trim(),
    logo_url: logoUrl.length > 0 ? logoUrl : null,
    favicon_url: faviconUrl.length > 0 ? faviconUrl : null,
    default_og_image_url: ogImageUrl.length > 0 ? ogImageUrl : null,
  } satisfies Json;
}

function serializeNavigationShortcut(content: NavigationShortcut): Json {
  return {
    label: content.label.trim(),
    href: content.href.trim(),
  } satisfies Json;
}

function serializeNavigationContent(content: NavigationContent): Json {
  return {
    links: content.links
      .map((entry) => {
        const label = entry.label.trim();
        const order = Number.isFinite(entry.order) ? entry.order : null;
        const base = {
          label,
          enabled: entry.enabled,
          external: entry.external,
          order,
          action: entry.action ?? 'link',
          style: entry.style ?? 'link',
        };
        const children = (entry.children ?? [])
          .map((child, index) => {
            const labelText = child.label.trim();
            const hrefText = child.href.trim();
            const propertyId = child.propertyId?.trim() ?? '';
            const orderValue = Number.isFinite(child.order) ? child.order : index + 1;
            const baseChild = {
              label: labelText,
              enabled: child.enabled,
              external: child.external,
              order: orderValue,
            };

            if (propertyId.length > 0) {
              return {
                ...baseChild,
                href: '',
                external: false,
                property_id: propertyId,
              };
            }

            if (labelText.length === 0 || hrefText.length === 0) {
              return null;
            }

            return {
              ...baseChild,
              href: hrefText,
            };
          })
          .filter((child): child is SerializedNavigationChild => child !== null);

        if (children.length > 0) {
          return { ...base, children };
        }

        return { ...base, href: entry.href.trim() };
      })
      .filter((entry) => {
        if (entry.label.length === 0) {
          return false;
        }
        if ('children' in entry && Array.isArray(entry.children)) {
          return entry.children.length > 0;
        }
        if (entry.action === 'call' || entry.action === 'auth') {
          return true;
        }
        if ('href' in entry && typeof entry.href === 'string') {
          return entry.href.length > 0;
        }
        return false;
      }),
    mobile_title: content.mobileTitle.trim(),
    mobile_description: content.mobileDescription.trim(),
    mobile_shortcut_heading: content.mobileShortcutHeading.trim(),
    mobile_shortcut_description: content.mobileShortcutDescription.trim(),
    mobile_shortcut_primary: serializeNavigationShortcut(content.mobileShortcutPrimary),
    mobile_shortcut_secondary: serializeNavigationShortcut(content.mobileShortcutSecondary),
  } satisfies Json;
}

function serializeFooterContent(content: FooterContent): Json {
  return {
    company_name: content.companyName.trim(),
    copyright_text: content.copyrightText.trim(),
  } satisfies Json;
}

function serializeSeoContent(content: SeoContent): Json {
  return {
    default_description: content.defaultDescription.trim(),
    canonical_origin: content.canonicalOrigin.trim(),
  } satisfies Json;
}

function serializeHomeContent(content: HomeContent): Json {
  return {
    hero: {
      eyebrow: content.hero.eyebrow.trim(),
      title: content.hero.title.trim(),
      description: content.hero.description.trim(),
    },
    availability: {
      eyebrow: content.availability.eyebrow.trim(),
      title: content.availability.title.trim(),
      description: content.availability.description.trim(),
      cta_label: content.availability.ctaLabel.trim(),
      cta_href: content.availability.ctaHref.trim(),
    },
    contact: {
      heading: content.contact.heading.trim(),
      description: content.contact.description.trim(),
    },
    seo: {
      title: content.seo.title.trim(),
      description: content.seo.description.trim(),
    },
    hero_media: {
      label: content.heroMedia.label.trim(),
      location: content.heroMedia.location.trim(),
      aria_label: content.heroMedia.ariaLabel.trim(),
      url: content.heroMedia.url.trim(),
      media_type: content.heroMedia.mediaType,
    },
    availability_messages: {
      empty_message: content.availabilityMessages.emptyMessage.trim(),
      error_message: content.availabilityMessages.errorMessage.trim(),
    },
  } satisfies Json;
}

function serializeLeadCapture(content: LeadCaptureContent): Json {
  return {
    eyebrow: content.eyebrow.trim(),
    title: content.title.trim(),
    description: content.description.trim(),
    thank_you: content.thankYou.trim(),
    seo: {
      title: content.seo.title.trim(),
      description: content.seo.description.trim(),
    },
    thank_you_title: content.thankYouTitle.trim(),
    benefits_heading: content.benefitsHeading.trim(),
    benefits: serializeTitleDescriptionList(content.benefits),
    stats: serializeValueLabelList(content.stats),
    form: {
      full_name_label: content.form.fullNameLabel.trim(),
      email_label: content.form.emailLabel.trim(),
      phone_label: content.form.phoneLabel.trim(),
      budget_label: content.form.budgetLabel.trim(),
      move_in_label: content.form.moveInLabel.trim(),
      notes_label: content.form.notesLabel.trim(),
      submit_label: content.form.submitLabel.trim(),
      consent_text: content.form.consentText.trim(),
      success_toast: content.form.successToast.trim(),
      error_toast: content.form.errorToast.trim(),
    },
  } satisfies Json;
}

function serializeAboutContent(content: AboutContent): Json {
  return {
    hero_title: content.heroTitle.trim(),
    hero_subtitle: content.heroSubtitle.trim(),
    tagline: content.tagline.trim(),
    hero_primary_cta_label: content.heroPrimaryCtaLabel.trim(),
    hero_primary_cta_href: content.heroPrimaryCtaHref.trim(),
    hero_secondary_cta_label: content.heroSecondaryCtaLabel.trim(),
    hero_secondary_cta_href: content.heroSecondaryCtaHref.trim(),
    highlight_title: content.highlightTitle.trim(),
    highlight_description: content.highlightDescription.trim(),
    featured_properties: serializeStringList(content.featuredProperties),
    snapshot_eyebrow: content.snapshotEyebrow.trim(),
    snapshot_stats: serializeLabelValueList(content.snapshotStats),
    body_heading: content.bodyHeading.trim(),
    body_description: content.bodyDescription.trim(),
    paragraphs: serializeStringList(content.paragraphs),
    details_summary_label: content.detailsSummaryLabel.trim(),
    promise_label: content.promiseLabel.trim(),
    seo: {
      title: content.seo.title.trim(),
      description: content.seo.description.trim(),
    },
    values: {
      title: content.values.title.trim(),
      description: content.values.description.trim(),
      cta_label: content.values.ctaLabel.trim(),
      cta_href: content.values.ctaHref.trim(),
      pillars: serializeTitleDescriptionList(content.values.pillars),
    },
    portfolio: {
      title: content.portfolio.title.trim(),
      description: content.portfolio.description.trim(),
      cta_label: content.portfolio.ctaLabel.trim(),
      cta_href: content.portfolio.ctaHref.trim(),
      flagship_eyebrow: content.portfolio.flagshipEyebrow.trim(),
      flagship_title: content.portfolio.flagshipTitle.trim(),
      flagship_description: content.portfolio.flagshipDescription.trim(),
      flagship_stats: serializeLabelValueList(content.portfolio.flagshipStats),
      flagship_features_heading: content.portfolio.flagshipFeaturesHeading.trim(),
      flagship_features_toggle_label: content.portfolio.flagshipFeaturesToggleLabel.trim(),
      flagship_features: serializeStringList(content.portfolio.flagshipFeatures),
      downtown_eyebrow: content.portfolio.downtownEyebrow.trim(),
      downtown_title: content.portfolio.downtownTitle.trim(),
      downtown_description: content.portfolio.downtownDescription.trim(),
      downtown_ideal_heading: content.portfolio.downtownIdealHeading.trim(),
      downtown_tenants: serializeStringList(content.portfolio.downtownTenants),
      downtown_addresses_heading: content.portfolio.downtownAddressesHeading.trim(),
      retail_eyebrow: content.portfolio.retailEyebrow.trim(),
      retail_title: content.portfolio.retailTitle.trim(),
      retail_description: content.portfolio.retailDescription.trim(),
      retail_plazas: serializeStringList(content.portfolio.retailPlazas),
      retail_footer: content.portfolio.retailFooter.trim(),
      retail_footer_link_label: content.portfolio.retailFooterLinkLabel.trim(),
    },
    contact_heading: content.contactHeading.trim(),
    contact_description: content.contactDescription.trim(),
  } satisfies Json;
}

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function handleSupabaseError(error: unknown): never {
  if (isPostgrestError(error)) {
    throw fromPostgrestError(error);
  }
  throw normalizeError(error);
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof (error as { code?: unknown }).code === 'string' &&
    typeof (error as { message?: unknown }).message === 'string'
  );
}
